import React, { useState } from 'react';
import { Spinner, PrimaryButton, DefaultButton, IconButton, Label, Stack } from '@fluentui/react';
import { Formik, Form, FieldArray } from 'formik';
import * as Yup from 'yup';
import useAxios from 'axios-hooks';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { FixedSizeList as List } from 'react-window';
import PropTypes from 'prop-types';
import TextField from '../../../Forms/TextField';
import ErrorComponent from '../../../Util/ErrorComponent';
import SuccessComponent from '../../../Util/SuccessComponent';

// dbo.CampaignLandingPages.Persona is NVARCHAR(50); Slug and LandingPageUrl are NVARCHAR(MAX).
const PERSONA_MAX_LENGTH = 50;

const DIMENSION_OPTIONS = [
    { value: 'Unit', label: 'Unit' },
    { value: 'Persona', label: 'Persona' },
];

const emptyLandingPage = { campaignLandingPageId: null, unitId: null, persona: '', country: null, landingPageUrl: '' };

const newCampaignObject = {
    campaignId: null,
    slug: '',
    dimension: 'Unit',
    landingPages: [{ ...emptyLandingPage }],
};

const selectStyles = {
    control: (base) => ({
        ...base,
        border: '1px solid #aaaaaa',
        minHeight: 32,
    }),
};

// Virtualized menu so large option lists (there are 80,000+ units) render without
// mounting every row at once.
const CustomMenuList = ({ children, ...props }) => {
    const height = 35;
    const itemCount = Array.isArray(children) ? children.length : 0;
    const totalHeight = Math.min(300, itemCount * height);

    if (!Array.isArray(children)) {
        return <div {...props}>{children}</div>;
    }

    return (
        <List height={totalHeight} itemCount={itemCount} itemSize={height} width="100%">
            {({ index, style }) => (
                <div
                    style={{
                        ...style,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        padding: '0 8px',
                        boxSizing: 'border-box',
                    }}
                    title={children[index]?.props?.children}
                >
                    {children[index]}
                </div>
            )}
        </List>
    );
};

CustomMenuList.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.element),
        PropTypes.node,
    ]),
};

const Campaign = () => {
    const { campaignId } = useParams();
    const isNew = campaignId === 'wizard';
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('Errors in form, check the highlighted fields.');

    const [{ data: campaign, loading: loadingCampaign }] = useAxios(
        `/internal/campaigns/${campaignId}`,
        { manual: isNew }
    );

    // Existing campaigns are used to enforce slug uniqueness on the client.
    const [{ data: existingCampaigns }] = useAxios('/internal/campaigns');

    const [{ data: units, loading: loadingUnits }] = useAxios('/internal/cobrand/all-client-schools');
    const unitOptions = (units ?? [])
        .map((u) => ({ value: u.clientSchoolId, label: u.schoolName }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const [{ data: countries, loading: loadingCountries }] = useAxios('courses/countries');
    const countryOptions = (countries ?? []).map((c) => ({ value: c.id, label: c.name }));

    const [, submitForm] = useAxios({ url: '/internal/campaigns' }, { manual: true });

    const slugSchema = Yup.string()
        .trim()
        .required('Slug is required')
        .test('unique-slug', 'A campaign with this slug already exists', function (value) {
            if (!value) return true;
            const normalized = value.trim().toLowerCase();
            return !(existingCampaigns ?? []).some(
                (c) =>
                    c.slug?.toLowerCase() === normalized &&
                    c.campaignId !== (campaign?.campaignId ?? null)
            );
        });

    // Row validation depends on the campaign's chosen dimension, so build the schema lazily
    // from the current form values (Yup 0.32 does not give array items access to parent fields).
    const validationSchema = Yup.lazy((formValues) => {
        const isPersona = formValues?.dimension === 'Persona';

        return Yup.object().shape({
            slug: slugSchema,
            dimension: Yup.string().required('Required'),
            landingPages: Yup.array()
                .of(
                    Yup.object().shape({
                        landingPageUrl: Yup.string().trim().required('Landing Page URL is required').url('Must be a valid URL'),
                        unitId: isPersona
                            ? Yup.number().nullable()
                            : Yup.number().nullable().required('Unit is required'),
                        persona: isPersona
                            ? Yup.string()
                                .trim()
                                .required('Persona is required')
                                .max(PERSONA_MAX_LENGTH, `Persona must be ${PERSONA_MAX_LENGTH} characters or fewer`)
                            : Yup.string().nullable().max(PERSONA_MAX_LENGTH, `Persona must be ${PERSONA_MAX_LENGTH} characters or fewer`),
                    })
                )
                .min(1, 'At least one landing page is required'),
        });
    });

    if (!isNew && loadingCampaign) {
        return <Spinner className="absolute-center" label="Loading Campaign..." />;
    }

    const initialValues = !isNew && campaign
        ? {
            campaignId: campaign.campaignId,
            slug: campaign.slug ?? '',
            dimension: campaign.dimension ?? 'Unit',
            landingPages:
                campaign.landingPages && campaign.landingPages.length > 0
                    ? campaign.landingPages.map((lp) => ({
                        campaignLandingPageId: lp.campaignLandingPageId ?? null,
                        unitId: lp.unitId ?? null,
                        persona: lp.persona ?? '',
                        country: lp.country ?? null,
                        landingPageUrl: lp.landingPageUrl ?? '',
                    }))
                    : [{ ...emptyLandingPage }],
        }
        : newCampaignObject;

    return (
        <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={validationSchema}
            validateOnBlur
            onSubmit={async (values) => {
                setShowError(false);
                const payload = {
                    campaignId: values.campaignId,
                    slug: values.slug.trim(),
                    dimension: values.dimension,
                    landingPages: values.landingPages.map((lp) => ({
                        campaignLandingPageId: lp.campaignLandingPageId,
                        unitId: values.dimension === 'Unit' ? lp.unitId : null,
                        persona: values.dimension === 'Persona' ? lp.persona?.trim() || null : null,
                        country: lp.country || null,
                        landingPageUrl: lp.landingPageUrl.trim(),
                    })),
                };

                const result = await submitForm({
                    url: '/internal/campaigns',
                    method: values.campaignId ? 'PUT' : 'POST',
                    data: payload,
                });

                if (!result.isAxiosError) {
                    setShowSuccess(true);
                    if (!values.campaignId) {
                        window.location.href = '/campaigns-feature/campaigns/' + result.data;
                    }
                } else {
                    const apiErrors = result.response?.data?.errors;
                    if (apiErrors && apiErrors.length > 0) {
                        setErrorMessage(apiErrors.map((e) => e.errorMessage).join(' '));
                    } else {
                        setErrorMessage('An error occurred while saving the campaign.');
                    }
                    setShowError(true);
                }
            }}
        >
            {({ values, isValid, dirty, setFieldValue, handleSubmit }) => (
                <Form style={{ maxWidth: '900px', marginBottom: 200 }}>
                    <SuccessComponent
                        successMessage="Campaign saved."
                        showSuccess={showSuccess}
                        setShowSuccess={setShowSuccess}
                    />
                    <ErrorComponent
                        errorMessage={errorMessage}
                        showError={showError}
                        setShowError={setShowError}
                    />

                    <TextField label="Slug" placeholder="Enter slug" type="text" name="slug" />

                    <Stack.Item style={{ marginTop: 12 }}>
                        <Label>Runs Off</Label>
                        <Select
                            options={DIMENSION_OPTIONS}
                            styles={selectStyles}
                            value={DIMENSION_OPTIONS.find((o) => o.value === values.dimension)}
                            onChange={(option) => setFieldValue('dimension', option.value)}
                        />
                    </Stack.Item>

                    <Label style={{ marginTop: 16 }}>Landing Pages</Label>
                    <FieldArray
                        name="landingPages"
                        render={(arrayHelpers) => (
                            <div>
                                {values.landingPages.map((lp, index) => (
                                    <Stack
                                        key={index}
                                        horizontal
                                        verticalAlign="end"
                                        tokens={{ childrenGap: 10 }}
                                        style={{
                                            marginBottom: 12,
                                            padding: 10,
                                            border: '1px solid #eee',
                                            borderRadius: 4,
                                        }}
                                    >
                                        {values.dimension === 'Unit' ? (
                                            <Stack.Item style={{ minWidth: 220 }}>
                                                <Label>Unit</Label>
                                                <Select
                                                    components={{ MenuList: CustomMenuList }}
                                                    options={unitOptions}
                                                    isDisabled={loadingUnits}
                                                    isSearchable
                                                    placeholder="Select unit"
                                                    styles={selectStyles}
                                                    value={unitOptions.find((o) => o.value === lp.unitId) || null}
                                                    onChange={(option) =>
                                                        setFieldValue(`landingPages.${index}.unitId`, option ? option.value : null)
                                                    }
                                                />
                                            </Stack.Item>
                                        ) : (
                                            <Stack.Item style={{ minWidth: 220 }}>
                                                <TextField
                                                    label="Persona"
                                                    placeholder="e.g. fox"
                                                    type="text"
                                                    name={`landingPages.${index}.persona`}
                                                    maxLength={PERSONA_MAX_LENGTH}
                                                />
                                            </Stack.Item>
                                        )}

                                        <Stack.Item style={{ minWidth: 200 }}>
                                            <Label>Country (optional)</Label>
                                            <Select
                                                options={countryOptions}
                                                isDisabled={loadingCountries}
                                                isClearable
                                                isSearchable
                                                placeholder="All countries"
                                                styles={selectStyles}
                                                value={countryOptions.find((o) => o.value === lp.country) || null}
                                                onChange={(option) =>
                                                    setFieldValue(`landingPages.${index}.country`, option ? option.value : null)
                                                }
                                            />
                                        </Stack.Item>

                                        <Stack.Item grow style={{ minWidth: 240 }}>
                                            <TextField
                                                label="Landing Page URL"
                                                placeholder="https://..."
                                                type="text"
                                                name={`landingPages.${index}.landingPageUrl`}
                                            />
                                        </Stack.Item>

                                        <Stack.Item>
                                            <IconButton
                                                iconProps={{ iconName: 'Delete' }}
                                                title="Remove"
                                                ariaLabel="Remove landing page"
                                                disabled={values.landingPages.length === 1}
                                                onClick={() => arrayHelpers.remove(index)}
                                            />
                                        </Stack.Item>
                                    </Stack>
                                ))}

                                <DefaultButton
                                    iconProps={{ iconName: 'Add' }}
                                    text="Add Landing Page"
                                    onClick={() => arrayHelpers.push({ ...emptyLandingPage })}
                                />
                            </div>
                        )}
                    />

                    <div style={{ marginTop: 20 }}>
                        <PrimaryButton
                            onClick={() => handleSubmit()}
                            text={values.campaignId ? 'Update' : 'Save'}
                            disabled={!isValid || !dirty}
                        />
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default Campaign;
